import { apiUrl } from "./api";

export interface GaugeLeaderboardItem {
  metric_name: string;
  pilot_uid: string;
  pilot_name: string | null;
  value: number;
  recorded_at: string | null;
}

export interface GaugeLeaderboardResponse {
  gauge_id: string;
  items: GaugeLeaderboardItem[];
}

export interface FetchGaugeLeaderboardParams {
  gaugeId: string;
  pilotUid?: string;
  limit?: number;
  direction?: "asc" | "desc";
}

export interface GaugeLeader {
  pilot_uid: string;
  pilot_name: string | null;
  value: number;
  recorded_at: string | null;
}

export interface GaugePilotStanding {
  value: number;
  recorded_at: string | null;
  rank: number | null;
  of: number | null;
}

export interface GaugeSummaryEntry {
  gauge_id: string;
  leader: GaugeLeader | null;
  // Present only when a pilot was requested; null when that pilot has no value for the gauge.
  pilot?: GaugePilotStanding | null;
}

export interface GaugeSummaryResponse {
  gauges: GaugeSummaryEntry[];
}

export interface PilotGaugesResponse {
  player_ucid: string;
  gauges: Record<string, { value: number; updated_at: string | null }>;
}

export async function fetchGaugeLeaderboard(params: FetchGaugeLeaderboardParams): Promise<GaugeLeaderboardResponse> {
  const url = apiUrl(`/gauges/${encodeURIComponent(params.gaugeId)}`);
  const query = new URLSearchParams();

  if (params.pilotUid) {
    query.set("pilot_uid", params.pilotUid);
  }

  if (params.limit != null) {
    query.set("limit", String(params.limit));
  }

  if (params.direction) {
    query.set("direction", params.direction);
  }

  url.search = query.toString();

  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  return (await response.json()) as GaugeLeaderboardResponse;
}

// Every gauge's leader in one request and, with a pilot, that pilot's own value and rank.
export async function fetchGaugeSummary(pilotUid?: string): Promise<GaugeSummaryResponse> {
  const url = apiUrl("/gauges");
  if (pilotUid) url.searchParams.set("pilot_uid", pilotUid);

  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  return (await response.json()) as GaugeSummaryResponse;
}

export async function fetchPilotGauges(playerUcid: string): Promise<PilotGaugesResponse> {
  const url = apiUrl("/pilot_gauges");
  const query = new URLSearchParams();
  query.set("player_ucid", playerUcid);
  url.search = query.toString();

  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  return (await response.json()) as PilotGaugesResponse;
}